
export enum EventType {
  WEDDING = 'Wedding',
  RECEPTION = 'Reception',
  ENGAGEMENT = 'Engagement',
  BIRTHDAY = 'Birthday Party',
  NAMING_CEREMONY = 'Naming Ceremony',
  HOUSE_WARMING = 'House Warming',
  CORPORATE = 'Corporate Event',
  HOUSE_PARTY = 'House Party',
  GET_TOGETHER = 'Get Together',
  POOJA = 'Pooja/Religious',
  HALDI_SANGEET = 'Haldi/Sangeet',
  OTHER = 'Other'
}

export enum PackageType {
  STANDARD = 250,
  PREMIUM = 350,
  CUSTOM = 0
}

export enum PaymentType {
  ADVANCE = 'Advance',
  PARTIAL = 'Partial',
  BALANCE = 'Balance',
  EXTRA = 'Extra'
}

export enum PaymentMode {
  UPI = 'UPI',
  BANK_TRANSFER = 'Bank Transfer',
  CASH = 'Cash'
}

export enum ExpenseCategory {
  // Event Specific
  VEGETABLES = 'Vegetables',
  GROCERIES = 'Groceries',
  DAIRY = 'Milk/Curd',
  GAS = 'Gas',
  STAFF = 'Staff Wages',
  TRANSPORT = 'Transport',
  RENTALS = 'Rentals',
  
  // Operations & Personal
  MISC = 'Misc',
  MARKETING = 'Marketing/Ads',
  RENT = 'Rent/Office',
  UTILITIES = 'Fuel/Phone/Bill',
  FOOD_PERSONAL = 'Personal Food',
  INVESTMENT = 'Investment/Asset',
  SALARY_FIXED = 'Fixed Salaries',
  MAINTENANCE = 'Repairs/Maint'
}

export interface Payment {
  id: string;
  eventId: string;
  type: PaymentType;
  amount: number;
  mode: PaymentMode;
  reference?: string;
  date: string;
  collectedBy: string;
}

export interface Expense {
  id: string;
  eventId: string | null; // null for global/personal expenses
  category: ExpenseCategory;
  amount: number;
  paidBy: string;
  mode: PaymentMode;
  date: string;
  description?: string;
}

export interface Event {
  id: string;
  customerName: string;
  phone: string;
  eventType: EventType;
  date: string;
  location: string;
  guestCount: number;
  packageType: PackageType | number;
  pricePerPlate: number;
  estimatedTotal: number;
  finalTotal: number;
  status: 'Upcoming' | 'Completed' | 'Cancelled';
}

// CRM TYPES
export type LeadStatus = 'HOT' | 'WARM' | 'COLD';
export type LeadStage = 'New' | 'Contacted' | 'Proposal Sent' | 'Negotiation' | 'Booked' | 'Lost';

export interface Lead {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  source: 'Website Chat' | 'Manual' | 'Google Sheet';
  status: LeadStatus;
  stage: LeadStage; // Current pipeline stage
  eventDate?: string; // Prospective event date
  confidenceScore: number;
  summary: string;
  aiReasoning: string;
  createdAt: string;
  chatHistory: { role: 'user' | 'ai'; text: string }[];
}

export interface KnowledgeItem {
  id: string;
  type: 'text' | 'file';
  name: string;
  content: string;
  status: 'indexed' | 'pending';
  dateAdded?: string;
}

export type AIProvider = 'groq' | 'openai' | 'gemini' | 'openrouter';

export interface AIConfig {
  activeProvider: AIProvider;
  providers: {
    [key in AIProvider]: {
      apiKey: string;
      model: string;
      enabled: boolean;
    }
  };
  systemCommands: string; // Global instructions
  emailSettings: {
    enabled: boolean;
    email: string;
    appPassword: string;
  };
  googleSheetsUrl?: string; // NEW: Stores the Web App URL
}

// Data Context Interface
export interface AppData {
  events: Event[];
  payments: Payment[];
  expenses: Expense[];
  leads: Lead[];
  knowledgeBase: KnowledgeItem[];
  aiConfig: AIConfig;
  
  // Actions
  addEvent: (event: Event) => void;
  updateEvent: (event: Event) => void;
  addPayment: (payment: Payment) => void;
  addExpense: (expense: Expense) => void;
  deletePayment: (id: string) => void;
  deleteExpense: (id: string) => void;
  deleteEvent: (id: string) => void;
  
  // CRM Actions
  addLead: (lead: Lead) => void;
  updateLead: (lead: Lead) => void;
  addKnowledge: (item: KnowledgeItem) => void;
  deleteKnowledge: (id: string) => void;
  updateAIConfig: (config: AIConfig) => void;

  getEventStats: (eventId: string) => EventStats;
  resetAllData: () => void;
  
  // Multi-lingual support
  language: 'en' | 'kn';
  setLanguage: (lang: 'en' | 'kn') => void;
  t: any; // Translation object
}

export interface EventStats {
  totalPaid: number;
  totalExpenses: number;
  balanceDue: number;
  netProfit: number;
  profitMargin: number;
  costPerPlate: number;
}
